/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { NotificationConfig, NotificationRef } from '@gh/core-ui';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/**
 * Status allows to manage notification lifecycle.
 * Status is necessary to attach notification effects and implements support of hiding after some delay.
 *
 * @internal this class is not intended for the public use
 *
 */
export enum NotificationStatus {
  // blocked notification is not closed on delay
  Blocked,
  // free notification can be closed on delay
  Free,
  // hiding effect was started
  Hiding,
  // notification is closed
  Closed,
}

/**
 * Internal notification object. It holds state of notification and allows to modify it.
 *
 * @internal this class is not intended for the public use
 */
export class Notification {
  readonly clicks = new Subject<void>();
  readonly afterClosed = new Subject<any>();

  private _status = new BehaviorSubject<NotificationStatus>(NotificationStatus.Free);

  constructor(public config: NotificationConfig) {

  }

  /**
   * Allows to track current status of notification. Look at {@link NotificationStatus} enumeration.
   *
   * @returns {Observable<NotificationStatus>}
   */
  get status(): Observable<NotificationStatus> {
    return this._status.asObservable();
  }

  touch(): void {
    this._status.next(NotificationStatus.Blocked);
    this._status.next(NotificationStatus.Free);
  }

  block(): void {
    this._status.next(NotificationStatus.Blocked);
  }

  free(): void {
    this._status.next(NotificationStatus.Free);
  }

  hiding(): void {
    this._status.next(NotificationStatus.Hiding);
  }

  close(): void {
    this.afterClosed.next(undefined);

    this.afterClosed.complete();
    this.clicks.complete();
    this._status.complete();
  }
}

/**
 * This class allows to manage notification lifecyle and handle notification events (clicks, afterClosed).
 *
 * This is a wrapper over internal notification object.
 */
export class DefaultNotificationRef implements NotificationRef {

  constructor(private notification: Notification) {

  }

  /**
   * Stream of clicks occurred on notification component
   *
   * @returns {Observable<MouseEvent>}
   */
  get clicks(): Observable<void> {
    return this.notification.clicks.asObservable();
  }

  /**
   * Stream that is emits strongly one value when notification is closed.
   * @returns {Observable<any>}
   */
  get afterClosed(): Observable<any> {
    return this.notification.afterClosed.asObservable();
  }

  /**
   * Closed this notification
   */
  close(): void {
    this.notification.close();
  }
}
